import { createAsyncThunk, isRejected } from '@reduxjs/toolkit';
import {
  actions as appStorageActions,
  records as appStorageRecords,
  utils as appStorageUtils,
} from '@saucelabs/sl-module-app-storage';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';

import actions from './actions';
import types from './types';

const deleteAndRefreshFiles = createAsyncThunk(
  types.DELETE_FILE,
  async (file: appStorageRecords.AppStorageFile, { dispatch, rejectWithValue }) => {
    try {
      const response = await dispatch(appStorageActions.deleteFile(file.id));

      // Refresh the files table after the delete request finished
      await dispatch(actions.fetchFiles({}));

      if (isRejected(response)) {
        const serverError = response.payload as appStorageUtils.SerializedApiError;
        throw serverError;
      }

      const deletedFile = response.payload.items[0];
      dispatch(notificationActions.notifySuccess(
        `Successfully deleted ${deletedFile.name} with ID: ${deletedFile.id}`,
        undefined,
        false,
        undefined,
        true,
      ));

      return deletedFile;
    } catch (error) {
      let apiError: appStorageUtils.SerializedApiError;

      if (error instanceof Error) {
        apiError = appStorageUtils.handleApiError(error);
      } else {
        apiError = error as appStorageUtils.SerializedApiError;
      }

      const {
        code,
        detail,
        title,
      } = apiError.error;

      dispatch(notificationActions.notifyDanger(
        `Deleting ${file.name} with ID ${file.id} did not complete as expected. ${detail}`,
        `${code} ${title}`,
        true,
        undefined,
        true,
      ));
      return rejectWithValue(apiError);
    }
  },
);

const updateFileDescription = (
  file: appStorageRecords.AppStorageFile,
  newDescription: string,
) => async (dispatch: ReduxDispatcher) => { // eslint-disable-line @typescript-eslint/explicit-module-boundary-types
  try {
    const response = await dispatch(appStorageActions.updateFile({
      fileId: file.id,
      unalteredFile: file,
      updatedFileProperties: {
        description: newDescription,
      },
    }));

    if (isRejected(response)) {
      const serverError = response.payload as appStorageUtils.SerializedApiError;
      throw serverError;
    }

    return response.payload;
  } catch (error) {
    let apiError: appStorageUtils.SerializedApiError;

    if (error instanceof Error) {
      apiError = appStorageUtils.handleApiError(error);
    } else {
      apiError = error as appStorageUtils.SerializedApiError;
    }

    const {
      code,
      detail,
      title,
    } = apiError.error;

    dispatch(notificationActions.notifyDanger(
      `Updating the description of the file with ID ${file.id} did not complete as expected. ${detail}`,
      `${code} ${title}`,
      true,
      undefined,
      true,
    ));

    return apiError;
  }
};

export default {
  deleteAndRefreshFiles,
  updateFileDescription,
};
